import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, Card, Button, Table, Tag, Modal, Space, Typography, message, Input, Select } from 'antd';
import { Link } from 'react-router-dom';
import * as Icon from 'react-feather';
import {
  curatedContentActionStarts,
  curatedContentActionErrors,
  getAllCuratedContentsAction,
  deleteCuratedContentAction,
} from '../../core/data/redux/modules/curatedContent/actions';
import { getAllCuratedContents, deleteCuratedContent } from '../../services/curatedContentServices';

const { Title } = Typography;
const { Option } = Select;

const ListCuratedContents = () => {
  const dispatch = useDispatch();
  const { curatedContents, loading, error } = useSelector((state: any) => state.curatedContent || { curatedContents: [], loading: false, error: null });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchCuratedContents();
  }, []);

  const fetchCuratedContents = async () => {
    dispatch(curatedContentActionStarts());
    try {
      const res = await getAllCuratedContents();
      if (res.error === true) throw new Error(res.message || 'Error fetching curated contents');
      const contentsData = res.data.items || res.data;
      dispatch(getAllCuratedContentsAction(contentsData));
    } catch (err: any) {
      dispatch(curatedContentActionErrors(err.message));
      message.error(err.message);
    }
  };

  const handleDelete = (content: any) => {
    setSelectedContent(content);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedContent) {
      try {
        await deleteCuratedContent(selectedContent.id);
        dispatch(deleteCuratedContentAction(selectedContent.id));
        message.success('Curated content deleted successfully');
        setDeleteModalOpen(false);
      } catch (err: any) {
        message.error(err.message);
      }
    }
  };

  const filteredContents = (curatedContents || []).filter((content: any) => {
    if (!content) return false;
    const matchesSearch = (content.content || content.title || content.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (content.type || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || content.publish_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      render: (content: string, record: any) => {
        const displayContent = content || record.title || record.name || 'No content';
        return (
          <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {displayContent?.substring(0, 100)}...
          </div>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag color="blue">{type || 'Unknown'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'publish_status',
      key: 'publish_status',
      render: (status: string) => {
        const color = status === 'PUBLISHED' ? 'green' : status === 'DRAFT' ? 'orange' : 'red';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Impressions',
      dataIndex: 'impression_count',
      key: 'impression_count',
      render: (count: number) => count || 0,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, content: any) => (
        <Space>
          <Link to={`/admin/curated-content/curated-content-detail/${content.id}`}>
            <Button icon={<Icon.Eye size={16} />} size="small" />
          </Link>
          <Link to={`/admin/curated-content/edit-curated-content/${content.id}`}>
            <Button icon={<Icon.Edit size={16} />} size="small" />
          </Link>
          <Button 
            icon={<Icon.Trash2 size={16} />} 
            size="small" 
            danger 
            onClick={() => handleDelete(content)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/admin/curated-content">Curated Content</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>List Curated Contents</Breadcrumb.Item>
        </Breadcrumb>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>Curated Contents</Title>
            <Link to="/admin/curated-content/create-curated-content">
              <Button type="primary" icon={<Icon.PlusCircle size={16} />}>Create Curated Content</Button>
            </Link>
          </div>
          
          <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Input
              placeholder="Search curated contents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 200 }}
              prefix={<Icon.Search size={16} />}
            />
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 120 }}
            >
              <Option value="all">All Status</Option>
              <Option value="DRAFT">Draft</Option>
              <Option value="PUBLISHED">Published</Option>
              <Option value="ARCHIVED">Archived</Option>
            </Select>
          </div>

          <Table
            columns={columns}
            dataSource={filteredContents}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
        
        <Modal
          title="Confirm Delete"
          open={deleteModalOpen}
          onOk={confirmDelete}
          onCancel={() => setDeleteModalOpen(false)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <p>Are you sure you want to delete this curated content?</p>
        </Modal>
      </div>
    </div>
  );
};

export default ListCuratedContents;
